/**
 * The story: full-screen cards with progress bars, like a phone story. Each
 * card plays, fills its bar and waits: tap the left third to go back,
 * anywhere else to go on, or use the arrows;
 * hold or press pause to stop the clock; swipe down or press Escape to leave.
 * The speaker opens a volume slider (↑ and ↓ set it too, M mutes).
 * Each card opens as a circle of its colour growing over the last one, as in
 * the concept video. On a wide screen the story stands in a current phone's
 * frame (9:19.5) with the arrows beside it.
 */

import { gsap } from "gsap";
import { ChevronLeft, ChevronRight, Music, Pause, Play, Volume1, Volume2, VolumeX, X } from "lucide-react";
import {
  Component,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
} from "react";

import { prefersReducedMotion } from "../motion";
import {
  getVolume,
  pauseMusic,
  play,
  playMusic,
  setMusic,
  setSoundOn,
  setVolume,
  soundOn,
  stopMusic,
  unlockSound,
  useMusicOn,
  useSound,
  useVolume,
} from "../sound";
import { CardColourProvider, PausedProvider, Stage } from "../story/stage";
import { ACT_LABELS, FONT_BODY, FONT_MONO, PAGE, lookFor } from "../theme";
import type { CardContext, CardSpec } from "./deck";
import { usePlayer } from "./usePlayer";

const HOLD_MS = 250;
const SWIPE_PX = 80;
const OPEN_S = 0.7;

/** A control on the story: its tap must not also count as a tap on the card. */
function Control({ label, onPress, disabled, expanded, className = "", children }: {
  label: string;
  onPress: () => void;
  disabled?: boolean;
  expanded?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      aria-expanded={expanded}
      disabled={disabled}
      className={`flex items-center justify-center rounded-full transition-opacity disabled:opacity-25 ${className}`}
      onPointerDown={(event) => event.stopPropagation()}
      onClick={(event) => {
        event.stopPropagation();
        unlockSound();
        onPress();
      }}
    >
      {children}
    </button>
  );
}

const keep = (event: { stopPropagation: () => void }) => event.stopPropagation();

/**
 * The speaker: tap it for a volume slider with a mute switch beside it. Both
 * are remembered on this device; letting go of the slider plays a pop at the
 * new level, so you hear what you picked.
 */
function SoundControl({ open, onOpen, hasMusic }: { open: boolean; onOpen: (open: boolean) => void; hasMusic: boolean }) {
  const [soundIsOn, toggleSound] = useSound();
  const [volume, changeVolume] = useVolume();
  const [musicOn, toggleMusic] = useMusicOn();
  const silent = !soundIsOn || volume === 0;
  const Icon = silent ? VolumeX : volume < 0.5 ? Volume1 : Volume2;
  return (
    <span className="relative">
      <Control label="Sound" expanded={open} onPress={() => onOpen(!open)} className="h-8 w-8 hover:bg-current/15">
        <Icon size={16} />
      </Control>
      {open ? (
        <div
          role="group"
          aria-label="Sound"
          className="absolute right-0 top-9 z-30 flex items-center gap-2 rounded-full py-1.5 pl-1.5 pr-3 shadow-lg"
          style={{ background: "var(--wr-ink)", color: "var(--wr-white)" }}
          onPointerDown={keep}
          onClick={keep}
        >
          <Control label={soundIsOn ? "Mute sound" : "Turn sound on"} onPress={toggleSound} className="h-7 w-7 hover:bg-current/15">
            {soundIsOn ? <Volume2 size={15} /> : <VolumeX size={15} />}
          </Control>
          {hasMusic ? (
            <Control
              label={musicOn ? "Turn the song off" : "Turn the song on"}
              onPress={toggleMusic}
              className={`h-7 w-7 hover:bg-current/15${musicOn ? "" : " opacity-40"}`}
            >
              <Music size={15} />
            </Control>
          ) : null}
          <input
            type="range"
            aria-label="Volume"
            min={0}
            max={100}
            step={5}
            value={Math.round(volume * 100)}
            className="w-28"
            style={{ accentColor: "var(--wr-yellow)" }}
            onChange={(event) => {
              unlockSound();
              const level = Number(event.target.value) / 100;
              changeVolume(level);
              // Turning it up means wanting to hear it.
              if (level > 0 && !soundOn()) setSoundOn(true);
            }}
            onPointerUp={() => play("pop", 1.2)}
            onKeyUp={() => play("pop", 1.2)}
          />
        </div>
      ) : null}
    </span>
  );
}

/** One card's bad data costs that card, never the whole story. */
class CardBoundary extends Component<{ children: ReactNode }, { failed: boolean }> {
  state = { failed: false };

  static getDerivedStateFromError() {
    return { failed: true };
  }

  render() {
    if (!this.state.failed) return this.props.children;
    return (
      <div className="line" style={{ top: 900, textAlign: "center" }}>
        This card couldn't load.
      </div>
    );
  }
}

function CardView({ render, ctx }: { render: CardSpec["render"]; ctx: CardContext }) {
  return render(ctx);
}

const lookOf = (card: CardSpec) => card.look ?? lookFor(card.id);

export function Player({
  deck,
  title = "",
  music = null,
  onClose = () => window.location.assign("/"),
}: {
  deck: CardSpec[];
  title?: string;
  /** The story's background song, if there is one. */
  music?: string | null;
  onClose?: () => void;
}) {
  const player = usePlayer(deck);
  const { index, progress, paused, done } = player;
  // Once a card has played through, the way on gives a little nudge.
  const nudge = done ? " wr-nudge" : "";
  const [, toggleSound] = useSound();
  const [soundOpen, setSoundOpen] = useState(false);
  // The song starts on the first tap or key press (browsers won't play sound before
  // one), then pauses and carries on with the story, and stops when it closes.
  const songStarted = useRef(false);
  const pausedNow = useRef(paused);
  pausedNow.current = paused;
  useEffect(() => {
    setMusic(music);
    return () => stopMusic();
  }, [music]);
  useEffect(() => {
    if (!music) return;
    const wake = () => {
      unlockSound();
      songStarted.current = true;
      if (!pausedNow.current) playMusic();
    };
    window.addEventListener("pointerdown", wake, { capture: true, once: true });
    window.addEventListener("keydown", wake, { capture: true, once: true });
    return () => {
      window.removeEventListener("pointerdown", wake, { capture: true });
      window.removeEventListener("keydown", wake, { capture: true });
    };
  }, [music]);
  useEffect(() => {
    if (!songStarted.current) return;
    if (paused) pauseMusic();
    else playMusic();
  }, [paused]);
  const card = deck[index];
  const first = index === 0;
  const last = index === deck.length - 1;
  const press = useRef<{ y: number; held: boolean; timer?: ReturnType<typeof setTimeout> } | null>(null);
  const swallowClick = useRef(false);

  // The card underneath while the next one opens over it.
  const [under, setUnder] = useState<number | null>(null);
  const shown = useRef(index);
  const opening = useRef<HTMLDivElement | null>(null);

  useLayoutEffect(() => {
    if (shown.current === index) return;
    const was = shown.current;
    shown.current = index;
    // The air comes from the side the next card's circle opens on.
    const opensFrom = deck[index] ? lookOf(deck[index]).from[0] : 0.5;
    play("whoosh", 1, { pan: (opensFrom - 0.5) * 1.4, length: OPEN_S });
    const el = opening.current;
    if (!el || prefersReducedMotion() || !deck[index]) return;
    const [fx, fy] = lookOf(deck[index]).from;
    const at = `at ${fx * 100}% ${fy * 100}%`;
    setUnder(was);
    const done = () => {
      setUnder(null);
      gsap.set(el, { clearProps: "clipPath" });
    };
    // 150% of the frame's half-diagonal reaches every corner from any edge point.
    const tween = gsap.fromTo(
      el,
      { clipPath: `circle(0% ${at})` },
      { clipPath: `circle(150% ${at})`, duration: OPEN_S, ease: "power3.inOut", onComplete: done },
    );
    const fallback = setTimeout(done, OPEN_S * 1000 + 100);
    return () => {
      tween.kill();
      clearTimeout(fallback);
    };
  }, [index, deck]);

  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      unlockSound();
      if (event.key === "ArrowRight") player.next();
      else if (event.key === "ArrowLeft") player.prev();
      else if (event.key === " ") {
        event.preventDefault();
        player.toggle();
      } else if (event.key === "m" || event.key === "M") toggleSound();
      else if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        // Up and down set the volume, a tenth at a time.
        event.preventDefault();
        setVolume(Math.round((getVolume() + (event.key === "ArrowUp" ? 0.1 : -0.1)) * 10) / 10);
        if (getVolume() > 0) setSoundOn(true);
        play("pop", 1.2);
      } else if (event.key === "Escape") {
        if (soundOpen) setSoundOpen(false);
        else onClose();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [player, onClose, toggleSound, soundOpen]);

  const onPointerDown = (event: PointerEvent) => {
    unlockSound();
    const timer = setTimeout(() => {
      if (press.current) press.current.held = true;
      player.pause();
    }, HOLD_MS);
    press.current = { y: event.clientY, held: false, timer };
  };

  const onPointerUp = (event: PointerEvent) => {
    const started = press.current;
    press.current = null;
    if (!started) return;
    clearTimeout(started.timer);
    const dy = (event.clientY ?? 0) - (started.y ?? 0);
    if (started.held) player.resume();
    swallowClick.current = started.held || Math.abs(dy) > 10;
    if (dy > SWIPE_PX) onClose();
  };

  const onClick = (event: MouseEvent<HTMLElement>) => {
    if (swallowClick.current) {
      swallowClick.current = false;
      return;
    }
    // A tap away from the open volume slider closes it, and only that.
    if (soundOpen) {
      setSoundOpen(false);
      return;
    }
    const box = event.currentTarget.getBoundingClientRect();
    if (box.width > 0 && event.clientX - box.left < box.width / 3) player.prev();
    else player.next();
  };

  if (!card) return null;
  const look = lookOf(card);
  const ctx = { next: player.next };

  const layer = (at: number, top: boolean) => {
    const c = deck[at];
    const l = lookOf(c);
    return (
      <div
        key={c.id}
        ref={top ? (el) => { opening.current = el; } : undefined}
        className="absolute inset-0"
        style={{ background: l.bg, color: l.fg }}
      >
        <CardColourProvider value={l.bg}>
          <Stage>
            <CardBoundary>
              <CardView render={c.render} ctx={ctx} />
            </CardBoundary>
          </Stage>
        </CardColourProvider>
      </div>
    );
  };

  return (
    <div
      className="fixed inset-0 flex items-center justify-center gap-10 overflow-hidden"
      style={{ background: PAGE, color: "var(--wr-text)", fontFamily: FONT_BODY }}
    >
      <div className="hidden md:block">
        <Control label="Previous card" onPress={player.prev} disabled={first} className="wr-side-button h-14 w-14">
          <ChevronLeft size={28} />
        </Control>
      </div>

      <div className="wr-phone relative">
        <section
          aria-label="Wrapped story"
          className="wr-screen relative h-dvh w-screen select-none overflow-hidden md:h-[min(86dvh,920px)] md:w-[calc(min(86dvh,920px)*9/19.5)]"
          style={{ background: look.bg, touchAction: "none" }}
          onPointerDown={onPointerDown}
          onPointerUp={onPointerUp}
          onPointerCancel={() => {
            // The browser took the gesture (a scroll, say): undo a hold, never leave it paused.
            const started = press.current;
            press.current = null;
            if (!started) return;
            clearTimeout(started.timer);
            if (started.held) player.resume();
          }}
          onClick={onClick}
        >
          <PausedProvider value={paused}>
            {under !== null && deck[under] && under !== index ? layer(under, false) : null}
            {layer(index, true)}
          </PausedProvider>

          <div aria-hidden className="wr-island" />
          <header
            className="absolute inset-x-0 top-0 z-20 px-[3.7%] pt-[max(0.75rem,env(safe-area-inset-top))] transition-colors duration-300 md:pt-12"
            style={{ color: look.fg }}
          >
            <div className="flex gap-[3px]">
              {deck.map((c, i) => (
                <div key={c.id} data-testid="progress" className="h-[3px] flex-1 overflow-hidden rounded-full bg-current/30">
                  <div
                    className="h-full rounded-full bg-current"
                    style={{ width: `${(i < index ? 1 : i === index ? progress : 0) * 100}%` }}
                  />
                </div>
              ))}
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px] font-bold md:text-xs" style={{ fontFamily: FONT_MONO }}>
              <span className="flex items-center gap-1.5">
                <span aria-hidden className="wr-mark" />
                <span>Wrapped {title}</span>
                <span className="opacity-70">·</span>
                <span className="opacity-70">{ACT_LABELS[card.act]}</span>
              </span>
              <span className="flex items-center gap-0.5">
                <Control label={paused ? "Play" : "Pause"} onPress={player.toggle} className="h-8 w-8 hover:bg-current/15">
                  {paused ? <Play size={16} /> : <Pause size={16} />}
                </Control>
                <SoundControl open={soundOpen} onOpen={setSoundOpen} hasMusic={Boolean(music)} />
                <Control label="Close" onPress={onClose} className="h-8 w-8 hover:bg-current/15">
                  <X size={18} />
                </Control>
              </span>
            </div>
          </header>

          {/* On a phone the arrows sit at the bottom corners of the card. */}
          <div
            className="pointer-events-none absolute inset-x-0 bottom-0 z-20 flex justify-between px-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] md:hidden"
            style={{ color: look.fg }}
          >
            <div className="pointer-events-auto flex w-full justify-between">
              <Control label="Previous card" onPress={player.prev} disabled={first} className="h-10 w-10 bg-current/15">
                <ChevronLeft size={22} />
              </Control>
              <Control label="Next card" onPress={player.next} disabled={last} className={`h-10 w-10 bg-current/15${nudge}`}>
                <ChevronRight size={22} />
              </Control>
            </div>
          </div>
        </section>
      </div>

      <div className="hidden md:block">
        <Control label="Next card" onPress={player.next} disabled={last} className={`wr-side-button h-14 w-14${nudge}`}>
          <ChevronRight size={28} />
        </Control>
      </div>

      <p className="absolute bottom-3 left-0 right-0 hidden text-center text-xs opacity-60 md:block" style={{ fontFamily: FONT_MONO }}>
        ← → to move · Space to pause · M to mute · Esc to close
      </p>
    </div>
  );
}
